import * as momentTz from 'moment-timezone';
import { Component, EventEmitter, Inject, Input, Output, OnInit, OnChanges, SimpleChanges } from 'ng-metadata/core';

/**
 * @ngdoc directive
 * @name mwDateRangePicker
 * @module mw.components.dateRange
 *
 * @param {Date} [start-date] - 'The start date that was picker is listening on'
 * @param {Date} [end-date] - 'The end date that was picker is listening on'
 * @param {string} [locale] - 'The language that the date picker will be shown in'
 * @param {string} [min-view] - 'The starting view of when the user opens up the date picker'
 * @param {string} [max-view] - 'How deep the user can go into the calendar all the way to seconds'
 * @param {string} [start-view] - 'View in which the calendar starts at'
 * @param {string} [format] - 'The format of how the date will be presented'
 * @param {Date} [min-date] - 'The minimum date that the user can select'
 * @param {Date} [max-date] - 'The maximum date that the user can select'
 * @param {Date} [max-days] - 'The maximum number of days the user can select'
 * @param {string} [timezone] - Timezone to be used by date range picker. Valid momentjs
 *    timezones (see https://momentjs.com/timezone/docs/#/using-timezones/parsing-in-zone/) are supported.  If not specified, the timezone of the browser will be used.
 * @param {EventEmitter} [on-start-date-selection] - 'What to do when the start date is selected'
 * @param {EventEmitter} [on-end-date-selection] - 'What to do when the date is selected'
 * @usage
 * <hljs hljs-language="html">
 *  <mw-date-range-picker
 *      date="new Date()"
 *      locale="en"
 *      min-view="month"
 *      max-view="month"
 *      start-view="month"
 *      format="L LTS"
 *      on-start-date-selection="$ctrl.handleStartDateChange($event)"
 *      on-end-date-selection="$ctrl.handleEndDateChange($event)"
 * > </mw-date-range-picker>
 * </hljs>
 */
@Component({
    selector: `mw-date-range-picker`,
    template: `
        <md-tabs md-dynamic-height md-border-bottom md-stretch-tabs="always" class="mw-date-picker-tabs" md-selected="$ctrl.selectedTab">
            <md-tab class="mw-date-range-picker__start-date-tab" label="{{$ctrl.startDateTabLabel}}">
                <mw-date-picker
                    class="mw-date-range-picker__start-date-picker"
                    date="$ctrl.startDate"
                    end-date="$ctrl.endDate"
                    start-date="$ctrl.startDate"
                    timezone="$ctrl.timezone"
                    min-date="$ctrl.minDate"
                    max-date="$ctrl.maxDate"
                    min-view="$ctrl.minView"
                    max-view="$ctrl.maxView"
                    start-view="$ctrl.startDateStartView"
                    locale="$ctrl.locale"
                    on-select="$ctrl.handleSelectStartDate($event.date)"
                    on-hover-date="$ctrl.handleHoverStartDate($event.date)"
                    on-mouse-leave-dates="$ctrl.handleMouseLeaveDates()"
                    >
                </mw-date-picker>
            </md-tab>
            <md-tab class="mw-date-range-picker__end-date-tab" label="{{$ctrl.endDateTabLabel}}" ng-disabled="!$ctrl.startDate">
                <mw-date-picker class="mw-date-range-picker__end-date-picker"
                    date="$ctrl.endDate"
                    end-date="$ctrl.endDate"
                    start-date="$ctrl.startDate"
                    timezone="$ctrl.timezone"
                    min-date="$ctrl.startDate || $ctrl.minDate"
                    max-date="$ctrl.endDateMaxDate"
                    min-view="$ctrl.minView"
                    max-view="$ctrl.maxView"
                    start-view="$ctrl.endDateStartView"
                    active-view-date="$ctrl.endDateActiveViewDate"
                    locale="$ctrl.locale"
                    on-select="$ctrl.handleSelectEndDate($event.date)"
                    on-hover-date="$ctrl.handleHoverEndDate($event.date)"
                    on-mouse-leave-dates="$ctrl.handleMouseLeaveDates()"
                    >
                </mw-date-picker>
            </md-tab>
        </md-tabs>
    `
})
export class DateRangePickerComponent implements OnInit, OnChanges {
    @Input('<') startDate: Date | momentTz.Moment;
    @Input('<') endDate: Date | momentTz.Moment;
    @Input('<') locale: string;
    @Input('<') minView: string;
    @Input('<') maxView: string;
    @Input('<') minDate: Date;
    @Input('<') maxDate: Date;
    @Input('<') timezone: string;
    @Input('<') startView: string;
    @Input('<') maxDays: number;
    @Output() onSelectStartDate: EventEmitter<{date: Date | momentTz.Moment}> = new EventEmitter<{date: Date | momentTz.Moment}>();
    @Output() onSelectEndDate: EventEmitter<{date: Date | momentTz.Moment}> = new EventEmitter<{date: Date | momentTz.Moment}>();
    @Output() onSelect: EventEmitter<DateRange> = new EventEmitter<DateRange>();


    private resettingDate = false;
    private tabLabelFormat: string = 'MMM D';
    private startDateTabLabel: string = 'mw-date-rage-picker__start-date';
    private endDateTabLabel: string = 'mw-date-range-picker__end-date';
    private startDateStartView: string;
    private endDateStartView: string;
    private startDateActiveViewDate: Date | momentTz.Moment;
    private endDateActiveViewDate: Date | momentTz.Moment;
    private endDateMaxDate: Date;

    private selectedTab: Number = 0;

    private tabLabelYearFormat: string = 'MMM D, YYYY';

    constructor(
        @Inject('$scope') private $scope: ng.IScope,
        @Inject('$translate') private $translate
    ) {

    }

    ngOnInit() {
        this.locale = this.locale ? this.locale : 'en';
        this.startDateStartView = this.startView ? this.startView : 'month';
        this.endDateStartView = this.startView ? this.startView : 'month';
        this.locale = this.locale ? this.locale : 'en';
        this.timezone = this.timezone ? this.timezone : momentTz.tz.guess(); // If no timezone is passed in, let moment guess the timezone

        this.resetEndDate(this.endDate);
        this.updateTabLabels();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ((changes.startDate && !changes.startDate.isFirstChange()) || (changes.endDate && !changes.endDate.isFirstChange() && changes.endDate.currentValue !== true)) {
            this.updateTabLabels();
        }
    }

    private updateTabLabels() {
        let labelFormat = this.getLabelFormat(this.startDate, this.endDate);
        this.startDateTabLabel = this.startDate ? this.formatLocalizedDate(this.startDate, this.locale, labelFormat, this.timezone) : this.$translate.instant('mw-date-range-picker__start-date');
        this.endDateTabLabel = this.endDate ? this.formatLocalizedDate(this.endDate, this.locale, labelFormat, this.timezone) : this.$translate.instant('mw-date-range-picker__end-date');
    }

    private getLabelFormat(startDate, endDate) {
        let currentYear = new Date().getFullYear();

        let sd = startDate ? momentTz(startDate) : null;
        let ed = endDate ? momentTz(endDate) : null;

        // If either start or end date are not in the current year, we should use label format including year
        return ((sd && sd.year() !== currentYear) || (ed && ed.year() !== currentYear)) ? this.tabLabelYearFormat : this.tabLabelFormat;
    }

    private formatLocalizedDate(date, locale, format, timezone) {
        return (date && locale && format && timezone) ? momentTz(date).tz(timezone).locale(locale).format(format) : '';
    }

    handleSelectStartDate(startDate) {
        this.startDate = startDate ? momentTz(startDate).tz(this.timezone).locale(this.locale).startOf('day') : null;

        this.resetEndDate();

        this.updateTabLabels();

        this.selectedTab = 1;

        this.onSelectStartDate.emit({ date: this.startDate });
        this.handleRangeChange();
    }

    handleSelectEndDate(endDate) {
        this.endDate = endDate ? momentTz(endDate).tz(this.timezone).locale(this.locale).endOf('day') : null;

        this.updateTabLabels();
        if (!this.startDate) {
            this.selectedTab = 0;
        }

        this.onSelectEndDate.emit({ date: this.endDate });
        this.handleRangeChange();
    }

    handleHoverStartDate(date) {
        if (date && !this.startDate) {
            let labelFormat = this.getLabelFormat(date, this.endDate);
            this.startDateTabLabel = this.formatLocalizedDate(date, this.locale, labelFormat, this.timezone); //: ''; //this.$translate.instant('mw-date-range-picker__start-date');
        } else {
            this.updateTabLabels();
        }
    }

    handleHoverEndDate(date) {
        if (date && !this.endDate) {
            let labelFormat = this.getLabelFormat(this.startDate, date);
            this.endDateTabLabel = this.formatLocalizedDate(date, this.locale, labelFormat, this.timezone); // : ''; //this.$translate.instant('mw-date-range-picker__end-date');
        } else {
            this.updateTabLabels();
        }
    }

    handleMouseLeaveDates() {
        this.updateTabLabels();
    }

    handleRangeChange() {
        let updatedDateRange: DateRange;
        if (this.startDate && this.endDate) {
            updatedDateRange = {
                startDate: momentTz.isMoment(this.startDate) ? this.startDate.toDate() : this.startDate,
                endDate: momentTz.isMoment(this.endDate) ? this.endDate.toDate() : this.endDate,
                type: 'custom'
            }
        }

        this.onSelect.emit(updatedDateRange);
    }

    resetEndDate(endDate?) {
        this.endDate = endDate;
        this.endDateStartView = this.startView || 'month';
        this.endDateActiveViewDate = momentTz(this.endDate || this.startDate).tz(this.timezone).locale(this.locale);

        if (this.maxDays && this.startDate) {
            let maxDaysMoment = momentTz(this.startDate).tz(this.timezone).locale(this.locale).add(this.maxDays - 1, 'days');
            let maxDateMoment = this.maxDate ? momentTz(this.maxDate).tz(this.timezone) : null;
            this.endDateMaxDate = maxDateMoment && maxDateMoment.isBefore(maxDaysMoment) ? maxDateMoment.toDate() : maxDaysMoment.toDate();
        } else {
            this.endDateMaxDate = this.maxDate;
        }
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/date-range/date-range-picker/date-range-picker.component.ts